import React, { FC, useEffect } from "react";
import { Box, Button } from "@mui/material";
import {
  DataGridPro,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridToolbarContainer,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { ThemeProvider } from "@mui/material/styles";
import themeInarix from "../../../../StylesMuiInarix";
import { useTranslation } from "react-i18next";
import { State } from "../../../../../redux/slices";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  resetStateCreateUsers,
  setCreateUsersStep,
  CreateUsersStep,
  ManagerUsersConfigRows,
} from "../../../../../redux/slices/users/createUsers";

import {
  setManagerUserConfigRows,
  setManagerColumns,
  setTryingToCreateSomething,
  initialState,
  setCreateManagerValues,
  resetManagerRows,
} from "../../../../../redux/slices/users/managersUsers";
import AddIcon from "@mui/icons-material/Add";
import { ThunkDispatch } from "@reduxjs/toolkit";

import validator from "validator";
import { selectLocale } from "../../../../../redux/slices/layout";
import {
  createMultiManagerUsersWithActions,
  groupTemplateLooksLike,
} from "../../../../../redux/actions/users/managerAsyncActions/managerAsyncActions";
import {
  handleChangeEmail,
  handleChangeGroups,
  handleChangeRole,
  handleDeleteClick,
  handleSiteChange,
  handleSiteChangeToNull,
} from "../validator/UserManagersActionsPerColumns";

import ManagersConfigTableHead from "./Head";
import CreateManagersButton from "./CreateManagersButton";
import CreateManagerUsersColumnsRows from "./FetchingColumnsManagers";
import { stylesMUIManagers } from "../styles/managersCreationStyles";
import { CheckExpiredToken } from "../../../../../utils/authentication";
import { SetActionsLayout } from "../../../../layout/Layout";

const SetActions = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  return {
    setManagerRows: (value: ManagerUsersConfigRows[]) => {
      dispatch(setManagerUserConfigRows(value));
    },
    setSteps: (value: CreateUsersStep) => {
      dispatch(setCreateUsersStep(value));
    },
    cancel: () => {
      dispatch(resetStateCreateUsers());
    },
    setColumnIndex: (columns: any) => {
      dispatch(setManagerColumns(columns));
    },

    tryingToCreate: (trying: boolean) => {
      dispatch(setTryingToCreateSomething(trying));
    },

    createManagersService: (navigate: (arg0: string) => unknown) => {
      dispatch(createMultiManagerUsersWithActions({ navigate }));
    },

    getGroupTemplateLookslikes: () => {
      dispatch(groupTemplateLooksLike(null));
    },
    resetValuesCreateManagers: () => {
      dispatch(setCreateManagerValues(initialState.createManagers));
    },
    resetManagerRows: () => {
      dispatch(resetManagerRows());
    },
  };
};
export const infoValidator = (something: any[]) => something?.length === 0;
export const roleValidator = (role: number | undefined) =>
  role && role > 0 ? true : false;

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

export const StepConfigCreateManagerUsers: FC<any> = () => {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  const { users } = useSelector((state: State) => state.UsersReducer);
  const { createUserSteps } = useSelector(
    (state: State) => state.CreateUsersReducer,
  );
  const {
    managerColumns,
    managerUsersConfigRows,
    tryingToCreateSomething,
    createManagers,
    createErrorManagerAllInarix,
    groupTemplateRaw,
    createErrorManagerAllInarixShow,
  } = useSelector((state: State) => state.CreateManagersReducer);
  const { user } = useSelector((state: State) => state.Authentication);

  const locale = useSelector(selectLocale);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const { logoutClickHandler } = SetActionsLayout();
  const {
    setSteps,
    setColumnIndex,
    setManagerRows,
    tryingToCreate,
    createManagersService,
    resetValuesCreateManagers,
    getGroupTemplateLookslikes,
    resetManagerRows,
  } = SetActions();

  const isValidUsersConfigs = (rows: ManagerUsersConfigRows[]): boolean => {
    return rows.every(
      (row) => !!row.email && isValidEmail(row.email) && !!row.role,
    );
  };

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setManagerRows(
      managerUsersConfigRows.map((row) =>
        row.id === newRow.id ? updatedRow : row,
      ) as ManagerUsersConfigRows[],
    );
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const { rawColumns } = CreateManagerUsersColumnsRows(
    managerColumns,
    handleDeleteClick,
    handleChangeGroups,
    handleChangeEmail,
    handleChangeRole,
    handleSiteChange,
    handleSiteChangeToNull,
    setManagerRows,
  );

  useEffect(() => {
    CheckExpiredToken(logoutClickHandler, user);
    resetManagerRows();
    getGroupTemplateLookslikes();
    setSteps({ ...createUserSteps, place: "configs-managers" });

    window.onbeforeunload = function () {
      window.setTimeout(function () {
        window.location = "/create-users/" as any;
      }, 0);
      window.onbeforeunload = null;
    };
  }, []);

  const editToolbar = () => {
    const handleClick = () => {
      const id = Math.random();
      setManagerRows([
        ...managerUsersConfigRows,
        {
          id,
          email: "",
          role: "",
          assignedSite: null,
          assignedGroups: [],
          isNew: true,
        },
      ]);

      setRowModesModel((oldModel) => ({
        ...oldModel,
        [id]: { mode: GridRowModes.Edit, fieldToFocus: "email" },
      }));
    };

    return (
      <GridToolbarContainer>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          disabled={managerUsersConfigRows.length >= 100}
          onClick={handleClick}
        >
          {t("Create_site.config_users.add_user")}
        </Button>
      </GridToolbarContainer>
    );
  };

  const existingUserNames = users.data.map((users) => users.username);

  const theUsernameExistValidator = (username: string) =>
    existingUserNames.filter((val) => username.includes(val));

  const theUserIsGood = () => {
    const transformToValidate = managerUsersConfigRows.map(
      (validateRows, index) => ({
        email: validateRows.email,
        role: validateRows.role,
        assignedGroups: validateRows.assignedGroups,
        isTheassignedGroupsGood: true,
        isTheEmailOk: validator.isEmail(validateRows.email),
        isTheEmailExist:
          theUsernameExistValidator(validateRows.email).length >= 1
            ? true
            : false,
        istheRolOk: roleValidator(validateRows.role as number),
        rowPosition: index + 1,
      }),
    );
    return transformToValidate;
  };

  const encounterErrorEmail = theUserIsGood().filter(
    (rows) => rows.isTheEmailOk === false,
  );

  const encounterErrorEmailExist = theUserIsGood().filter(
    (rows) => rows.isTheEmailExist === true,
  );

  const encounterErrorSites = theUserIsGood().filter((rows) => false);

  const totalErrors = encounterErrorEmail?.concat(
    encounterErrorEmailExist?.concat(encounterErrorSites),
  ).length;

  return (
    <div className="create-sites-container">
      <div className="steps-container-create-loc">
        <div className="step-title">{t("Create_site.config_users.step")}</div>
        <div>
          <ManagersConfigTableHead
            totalErrors={totalErrors}
            tryingToCreateSomething={tryingToCreateSomething}
            createManagers={createManagers}
            resetValuesCreateManagers={resetValuesCreateManagers}
            createErrorManagerAllInarix={createErrorManagerAllInarix}
            createErrorManagerAllInarixShow={createErrorManagerAllInarixShow}
            theUserIsGood={theUserIsGood()}
          />
          <div
            className="create-site-form"
            style={stylesMUIManagers.StepConfigCreateManagerUsersForm}
          >
            <Box sx={stylesMUIManagers.StepConfigCreateManagerUsersFormBox}>
              <ThemeProvider theme={themeInarix(locale)}>
                <DataGridPro
                  loading={createManagers.createLoading}
                  rows={
                    managerUsersConfigRows.length === 0
                      ? []
                      : managerUsersConfigRows
                  }
                  columns={
                    rawColumns.length === 0
                      ? [
                          {
                            field: "id",
                            sortable: true,
                            width: 100,
                          },
                        ]
                      : rawColumns
                  }
                  onColumnOrderChange={() => {
                    setColumnIndex(apiRef.current.getAllColumns());
                  }}
                  onColumnWidthChange={() =>
                    setColumnIndex(apiRef.current.getAllColumns())
                  }
                  editMode="row"
                  rowModesModel={rowModesModel}
                  onRowModesModelChange={handleRowModesModelChange}
                  onRowEditStop={handleRowEditStop}
                  processRowUpdate={processRowUpdate}
                  pageSizeOptions={[25, 50, 100]}
                  slots={{
                    footer: editToolbar as any["footer"],
                  }}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                  apiRef={apiRef}
                  rowHeight={70}
                  rowSelection={false}
                  disableColumnSelector={true}
                  disableRowSelectionOnClick={true}
                />
              </ThemeProvider>
            </Box>
            <CreateManagersButton
              totalErrors={totalErrors}
              tryingToCreate={tryingToCreate}
              createManagersService={createManagersService}
              groupTemplateRaw={groupTemplateRaw}
              createManagers={createManagers}
              isValidUsersConfigs={isValidUsersConfigs}
              managerUsersConfigRows={managerUsersConfigRows}
              navigate={navigate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepConfigCreateManagerUsers;
